<template>
  <layout title="Sign Up">
    <div class="flex h-[100dvh] landscape:h-screen flex-col">
      <div class="relative flex min-h-full justify-center md:px-12 lg:px-0">
        <div
          class="relative z-10 flex flex-1 flex-col bg-white py-10 px-4 sm:justify-center md:flex-none md:px-28"
        >
          <div
            class="mx-auto w-full max-w-md sm:px-4 md:w-96 md:max-w-sm md:px-0"
          >
            <div class="flex flex-col">
              <div>
                <logo class="w-32" />
              </div>
              <div class="mt-10">
                <h2 class="text-lg font-semibold text-gray-900">
                  {{ t("auth.invitation.title") }}
                </h2>
                <p class="mt-2 text-sm text-gray-700">
                  {{ t("auth.invitation.description") }}
                </p>
              </div>
            </div>
            <form
              @submit.prevent="submit"
              class="mt-10 grid grid-cols-1 gap-y-8"
            >
              <div class="flex gap-2">
                <div>
                  <label
                    for="first_name"
                    class="mb-3 block text-sm font-medium text-gray-700"
                    >{{ t("auth.invitation.first_name") }}</label
                  ><input
                    id="first_name"
                    type="text"
                    name="first_name"
                    autocomplete="first_name"
                    v-model="form.first_name"
                    class="block w-full appearance-none rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-blue-500 sm:text-sm"
                  />
                  <p
                    v-if="form.errors.first_name"
                    class="mt-2 text-sm text-red-600"
                    id="email-error"
                  >
                    {{ form.errors.first_name }}
                  </p>
                </div>
                <div>
                  <label
                    for="last_name"
                    class="mb-3 block text-sm font-medium text-gray-700"
                    >{{ t("auth.invitation.last_name") }}</label
                  ><input
                    id="last_name"
                    type="text"
                    name="last_name"
                    autocomplete="last_name"
                    v-model="form.last_name"
                    class="block w-full appearance-none rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-blue-500 sm:text-sm"
                  />
                  <p
                    v-if="form.errors.last_name"
                    class="mt-2 text-sm text-red-600"
                    id="email-error"
                  >
                    {{ form.errors.last_name }}
                  </p>
                </div>
              </div>
              <div class="">
                <label
                  for="email"
                  class="mb-3 block text-sm font-medium text-gray-700"
                  >{{ t("auth.invitation.email_address") }}</label
                ><input
                  id="email"
                  type="email"
                  name="email"
                  autocomplete="email"
                  v-model="form.email"
                  class="block w-full appearance-none rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-blue-500 sm:text-sm"
                />
                <p
                  v-if="form.errors.email"
                  class="mt-2 text-sm text-red-600"
                  id="email-error"
                >
                  {{ form.errors.email }}
                </p>
              </div>
              <div class="">
                <label
                  for="password"
                  class="mb-3 block text-sm font-medium text-gray-700"
                  >{{ t("auth.invitation.password") }}</label
                ><input
                  id="password"
                  type="password"
                  name="password"
                  autocomplete="password"
                  v-model="form.password"
                  class="block w-full appearance-none rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-brand-900 focus:bg-white focus:outline-none focus:ring-blue-500 sm:text-sm"
                />
                <p
                  v-if="form.errors.password"
                  class="mt-2 text-sm text-red-600"
                  id="password-error"
                >
                  {{ form.errors.password }}
                </p>
              </div>
              <TermsAndPrivacy
                class="mt-2"
                :label="t('auth.invitation.continue')"
              />
              <div>
                <button
                  class="group inline-flex items-center justify-center rounded-full py-2 px-4 text-sm font-semibold focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 bg-brand-900 text-white hover:text-slate-100 hover:bg-blue-500 active:bg-blue-800 active:text-blue-100 focus-visible:outline-blue-600 w-full"
                  type="submit"
                >
                  <span
                    >{{ t("auth.invitation.continue") }}
                    <span aria-hidden="true">→</span></span
                  >
                </button>
              </div>
            </form>
          </div>
        </div>
        <div
          class="hidden sm:contents lg:relative lg:block lg:flex-1 bg-brand-900"
        ></div>
      </div>
    </div>
  </layout>
</template>

<script setup>
import Layout from "@/Tailwind/Layout";
import Logo from "@/Tailwind/Components/Logo";
import { useForm, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import "@lbgm/phone-number-input/style";
import { defineProps, inject, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useBrowserLocale } from "@/Tailwind/Mixins/useBrowserLocale";
import TermsAndPrivacy from "@/Tailwind/Components/TermsAndPrivacy.vue";

const props = defineProps({
  invitation: Object,
});

const i18n = useI18n();
const t = i18n.t;

const defaultLocale = usePage().props.locale;
const browserLocale = useBrowserLocale(i18n, defaultLocale);

const form = useForm({
  first_name: null,
  last_name: null,
  email: null,
  password: null,
});

onMounted(() => {
  // Set the current language as the prefered browser language
  i18n.locale.value = browserLocale;
});

const emitter = inject("emitter");

const submit = () => {
  form.put(route("invitations.update", { invitation: props.invitation }), {
    onSuccess: () => {
      emitter.emit("authenticated");
    },
  });
};
</script>
